"use client";

import { JSX, useMemo } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import type { ModuleHierarchyTreeData, ModuleFlatHierarchyNode } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import { accentToneOr } from "./module-accent";
import { legendIndexOfCategory } from "./shared/category-resolve";
import {
  useHierarchy,
  levelLegendItems,
  levelForFilter,
} from "./shared/hierarchies";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";

type Column = { label: string; levels: number[] };

function ColumnarView({
  nodes,
  columns,
}: {
  nodes: ModuleFlatHierarchyNode[];
  columns?: Column[];
}) {
  const node = useHoverSelect<string>();
  const { selected, effective } = node;
  const hoverGesture = useHoverGesture();
  const { t } = useViewerLanguage();

  const maxLevel = useMemo(
    () => nodes.reduce((m, n) => Math.max(m, n.level), 0),
    [nodes],
  );

  const effectiveColumns = useMemo<Column[]>(() => {
    if (columns && columns.length > 0) return columns;
    const levels = Array.from({ length: maxLevel + 1 }, (_, i) => i);
    return levels.map((lvl) => ({
      label:
        lvl === 0
          ? t("Root")
          : lvl === 1
            ? t("Department")
            : t("Level {level}", { level: lvl }),
      levels: [lvl],
    }));
  }, [columns, maxLevel, t]);

  const childrenOf = useMemo(() => {
    const c: Record<string, string[]> = {};
    nodes.forEach((n) => {
      if (n.parent) (c[n.parent] = c[n.parent] || []).push(n.id);
    });
    return c;
  }, [nodes]);

  const nodeById = useMemo(() => {
    const m: Record<string, ModuleFlatHierarchyNode> = {};
    nodes.forEach((n) => (m[n.id] = n));
    return m;
  }, [nodes]);

  const ancestorsOf = (id: string): string[] => {
    const path: string[] = [];
    let cur = nodeById[id];
    while (cur && cur.parent) {
      path.push(cur.parent);
      cur = nodeById[cur.parent];
    }
    return path;
  };

  const W = 760,
    H = 400;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [nodes, columns]);
  const padX = 16,
    padY = 20;
  const colHeaderH = 34;
  const nodeH = 28;
  const gap = 10;
  const nodeWFor = (label: string) => Math.max(72, label.length * 6.6 + 22);

  const colW =
    (W - padX * 2 - gap * (effectiveColumns.length - 1)) /
    effectiveColumns.length;

  const positions = useMemo(() => {
    const pos: Record<string, { x: number; y: number; w: number; colIndex: number }> = {};
    effectiveColumns.forEach((col, ci) => {
      const colX = padX + ci * (colW + gap);
      const idsInCol = nodes
        .filter((n) => col.levels.includes(n.level))
        .sort((a, b) => a.level - b.level)
        .map((n) => n.id);
      const availH = H - padY - colHeaderH - padY;
      const nBars = idsInCol.length;
      const step = nBars > 1 ? availH / (nBars - 1) : 0;
      idsInCol.forEach((id, i) => {
        const y =
          nBars === 1
            ? padY + colHeaderH + availH / 2
            : padY + colHeaderH + i * step;
        const w = nodeWFor(nodeById[id]?.label ?? "");
        const nodeX = colX + (colW - w) / 2;
        pos[id] = { x: nodeX, y, w, colIndex: ci };
      });
    });
    return pos;
  }, [effectiveColumns, colW, nodes, nodeById]);

  const hoveredPath = effective
    ? new Set([effective, ...ancestorsOf(effective)])
    : null;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${dynamicH}`}
      className="w-full h-auto"
      style={{ overflow: "visible" }}
    >
      {effectiveColumns.map((col, ci) => {
        const colX = padX + ci * (colW + gap);
        return (
          <g key={ci}>
            <rect
              x={colX}
              y={padY}
              width={colW}
              height={H - padY * 2}
              fill="var(--card)"
              fillOpacity="0.4"
              stroke="var(--grid-strong)"
              strokeWidth="1"
              strokeDasharray="4 4"
              rx="8"
            />
            <text
              x={colX + 14}
              y={padY + 20}
              fontSize="11"
              fill="var(--muted)"
              fontWeight="500"
            >
              {stripInlineMarkdown(col.label)}
            </text>
          </g>
        );
      })}
      {nodes.filter((n) => n.parent && positions[n.id] && positions[n.parent!]).map((n) => {
        const p = positions[n.parent!];
        const c = positions[n.id];
        const sameCol = p.colIndex === c.colIndex;
        const onPath = hoveredPath && hoveredPath.has(n.id) && hoveredPath.has(n.parent!);
        const dim = hoveredPath && !onPath;
        let d: string;
        if (sameCol) {
          const midY = (p.y + c.y) / 2;
          d = `M ${p.x + p.w} ${p.y} C ${p.x + p.w + 30} ${p.y}, ${c.x - 30} ${midY}, ${c.x} ${c.y}`;
        } else {
          const px = p.x + p.w;
          const cx = c.x;
          const midX = (px + cx) / 2;
          d = `M ${px} ${p.y} C ${midX} ${p.y}, ${midX} ${c.y}, ${cx} ${c.y}`;
        }
        return (
          <path
            key={n.id}
            d={d}
            fill="none"
            stroke={onPath ? "var(--chart-1)" : "var(--chart-3)"}
            strokeWidth={onPath ? 1.75 : 1.2}
            strokeOpacity={dim ? 0.12 : 0.75}
            strokeLinecap="round"
            style={{ transition: "all 0.15s" }}
          />
        );
      })}
      {nodes.filter((n) => positions[n.id]).map((n) => {
        const p = positions[n.id];
        const isHovered = effective === n.id;
        const onPath = hoveredPath && hoveredPath.has(n.id);
        const dim = hoveredPath && !onPath;
        const isRoot = n.level === 0;
        const stroke = isRoot
          ? "var(--chart-1)"
          : n.level === 1
            ? "var(--chart-2)"
            : "var(--chart-3)";
        return (
          <g
            key={n.id}
            onPointerEnter={(e) => {
              if (hoverGesture.enter(e)) node.setHovered(n.id);
            }}
            onPointerLeave={(e) => {
              if (hoverGesture.leave(e)) node.setHovered(null);
            }}
            onClick={(e) =>
              node.select(n.id, {
                kind: "node",
                id: n.id,
                element: e.currentTarget,
              })
            }
            style={{ cursor: "pointer", transition: "opacity 0.15s" }}
            opacity={dim ? 0.3 : 1}
          >
            {isHovered && (
              <rect
                x={p.x - 2}
                y={p.y - nodeH / 2 - 2}
                width={p.w + 4}
                height={nodeH + 4}
                rx="8"
                fill="none"
                stroke={stroke}
                strokeWidth="1"
                opacity="0.6"
              />
            )}
            <rect
              x={p.x}
              y={p.y - nodeH / 2}
              width={p.w}
              height={nodeH}
              rx="6"
              fill="var(--bg)"
              stroke={stroke}
              strokeWidth={onPath || isHovered ? 1.5 : 1}
            />
            <foreignObject
              x={p.x}
              y={p.y - nodeH / 2}
              width={p.w}
              height={nodeH}
              overflow="visible"
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: dim ? "var(--muted)" : "var(--fg)",
                  fontSize: "11px",
                  fontWeight: isRoot ? 600 : 500,
                  lineHeight: 1.15,
                  textAlign: "center",
                  overflowWrap: "anywhere",
                  padding: "0 6px",
                }}
              >
                {stripInlineMarkdown(n.label)}
              </div>
            </foreignObject>
          </g>
        );
      })}
    </svg>
  );
}

function BracketView({ nodes }: { nodes: ModuleFlatHierarchyNode[] }) {
  const selection = useSelectionGroup();
  const node = useHoverSelect<string>({ group: selection });
  const { selected, effective } = node;
  const legend = useLegendHoverSelect({ group: selection });
  const hoverGesture = useHoverGesture();

  const { nodeById, childrenOf, leaves, ancestorsOf, maxLevel } = useHierarchy(nodes);

  const { t } = useViewerLanguage();
  const levelLabels: [string, string, string] = [t("Root"), t("Department"), t("Team")];
  const W = 760,
    H = 380;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [nodes]);
  const padX = 20,
    padY = 30;
  const nodeW = 120,
    nodeH = 28;
  const availW = W - padX * 2 - nodeW;
  const xForLevel = (lvl: number) => padX + (availW / Math.max(1, maxLevel)) * lvl;

  const yByNode = useMemo(() => {
    const y: Record<string, number> = {};
    const availH = H - padY * 2;
    const step = leaves.length > 1 ? availH / (leaves.length - 1) : 0;
    leaves.forEach((n, i) => {
      y[n.id] = padY + i * step;
    });
    function compute(id: string): number {
      if (y[id] !== undefined) return y[id];
      const kids = childrenOf[id] || [];
      const ys = kids.map(compute);
      y[id] = ys.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : padY + availH / 2;
      return y[id];
    }
    nodes.forEach((n) => compute(n.id));
    return y;
  }, [nodes, childrenOf, leaves]);

  const legendItems = useMemo(
    () => levelLegendItems(nodes, levelLabels),
    [nodes, t],
  );
  const legFilter =
    legend.filter !== null ? legendItems[legend.filter]?.id ?? null : null;
  const selectedLevel = selected !== null ? nodeById[selected]?.level : undefined;
  const legendSelectedIndex =
    legend.selected ??
    legendIndexOfCategory(
      legendItems,
      selectedLevel !== undefined ? `l${selectedLevel}` : null,
    );
  const hoveredPath = effective
    ? new Set([effective, ...ancestorsOf(effective)])
    : null;

  return (
    <div className="w-full">
      <div className="relative">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${dynamicH}`} className="w-full h-auto" style={{ overflow: "visible" }}>
          {nodes
            .filter((n) => (childrenOf[n.id] || []).length > 0)
            .map((parent) => {
              const kids = childrenOf[parent.id];
              const px = xForLevel(parent.level) + nodeW;
              const py = yByNode[parent.id];
              const cx = xForLevel(parent.level + 1);
              const midX = px + (cx - px) * 0.55;
              const kidYs = kids.map((kid) => yByNode[kid]);
              const spineTop = Math.min(...kidYs);
              const spineBottom = Math.max(...kidYs);
              const parentOnPath = hoveredPath && hoveredPath.has(parent.id);
              const parentLegDim =
                legFilter !== null && levelForFilter[legFilter] !== parent.level;
              return (
                <g key={parent.id}>
                  <line
                    x1={px}
                    y1={py}
                    x2={midX}
                    y2={py}
                    stroke={parentOnPath ? "var(--chart-1)" : "var(--chart-3)"}
                    strokeWidth={parentOnPath ? 1.5 : 1}
                    strokeOpacity={(hoveredPath && !parentOnPath) || parentLegDim ? 0.15 : 0.85}
                    strokeLinecap="round"
                    style={{ transition: "all 0.15s" }}
                  />
                  <line
                    x1={midX}
                    y1={Math.min(py, spineTop)}
                    x2={midX}
                    y2={Math.max(py, spineBottom)}
                    stroke={parentOnPath ? "var(--chart-1)" : "var(--chart-3)"}
                    strokeWidth={parentOnPath ? 1.5 : 1}
                    strokeOpacity={(hoveredPath && !parentOnPath) || parentLegDim ? 0.15 : 0.85}
                    strokeLinecap="round"
                    style={{ transition: "all 0.15s" }}
                  />
                  {kids.map((kidId) => {
                    const kidY = yByNode[kidId];
                    const kid = nodeById[kidId];
                    const onPath = hoveredPath && hoveredPath.has(kidId) && parentOnPath;
                    const legDim =
                      legFilter !== null &&
                      levelForFilter[legFilter] !== kid.level &&
                      levelForFilter[legFilter] !== parent.level;
                    const dim = (hoveredPath && !onPath) || legDim;
                    return (
                      <line
                        key={kidId}
                        x1={midX}
                        y1={kidY}
                        x2={cx}
                        y2={kidY}
                        stroke={onPath ? "var(--chart-1)" : "var(--chart-3)"}
                        strokeWidth={onPath ? 1.5 : 1}
                        strokeOpacity={dim ? 0.15 : 0.85}
                        strokeLinecap="round"
                        style={{ transition: "all 0.15s" }}
                      />
                    );
                  })}
                </g>
              );
            })}
          {nodes.map((n) => {
            const x = xForLevel(n.level);
            const y = yByNode[n.id];
            const isHovered = effective === n.id;
            const onPath = hoveredPath && hoveredPath.has(n.id);
            const isRoot = n.level === 0;
            const legDim = legFilter !== null && levelForFilter[legFilter] !== n.level;
            const dim = (hoveredPath && !onPath) || legDim;
            const stroke = isRoot
              ? "var(--chart-1)"
              : n.level === 1
                ? "var(--chart-2)"
                : "var(--chart-3)";
            return (
              <g
                key={n.id}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) node.setHovered(n.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) node.setHovered(null);
                }}
                onClick={(e) =>
                  node.select(n.id, {
                    kind: "node",
                    id: n.id,
                    element: e.currentTarget,
                  })
                }
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.3 : 1}
              >
                <rect
                  x={x}
                  y={y - nodeH / 2}
                  width={nodeW}
                  height={nodeH}
                  rx="2"
                  fill="var(--card)"
                  stroke={stroke}
                  strokeWidth={onPath || isHovered ? 1.5 : 1}
                />
                <foreignObject
                  x={x}
                  y={y - nodeH / 2}
                  width={nodeW}
                  height={nodeH}
                  overflow="visible"
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: dim ? "var(--muted)" : "var(--fg)",
                      fontSize: "11px",
                      fontWeight: isRoot ? 600 : 500,
                      lineHeight: 1.15,
                      textAlign: "center",
                      overflowWrap: "anywhere",
                      padding: "0 6px",
                    }}
                  >
                    {stripInlineMarkdown(n.label)}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>
      <SwipeScroll className="mt-4 pt-4 border-t border-dashed border-[var(--border-dashed)]">
        <Legend
          items={legendItems}
          hoveredIndex={legend.hovered}
          onHoverChange={legend.setHovered}
          selectedIndex={legendSelectedIndex}
          onSelect={legend.select}
          onLabelRef={legend.setLabelRef}
          className="flex flex-row gap-1"
        >
          <LegendItem className="flex shrink-0 items-center gap-2 px-2.5 py-1.5">
            <LegendMarker />
            <LegendLabel />
            <LegendValue />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
  );
}

function NodeLinkView({ nodes, rootTone }: { nodes: ModuleFlatHierarchyNode[]; rootTone: string }) {
  const selection = useSelectionGroup();
  const node = useHoverSelect<string>({ group: selection });
  const { selected, effective } = node;
  const legend = useLegendHoverSelect({ group: selection });
  const hoverGesture = useHoverGesture();

  const { nodeById, childrenOf, leaves, ancestorsOf, maxLevel } = useHierarchy(nodes);

  const { t } = useViewerLanguage();
  const levelLabels: [string, string, string] = [t("Root"), t("Department"), t("Team")];
  const W = 760,
    H = 380;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [nodes]);
  const padX = 20,
    padY = 30;
  const nodeW = 130,
    nodeH = 32;
  const availW = W - padX * 2 - nodeW;
  const xForLevel = (lvl: number) => padX + (availW / Math.max(1, maxLevel)) * lvl;

  const yByNode = useMemo(() => {
    const y: Record<string, number> = {};
    const availH = H - padY * 2;
    const step = leaves.length > 1 ? availH / (leaves.length - 1) : 0;
    leaves.forEach((n, i) => {
      y[n.id] = padY + i * step;
    });
    function compute(id: string): number {
      if (y[id] !== undefined) return y[id];
      const kids = childrenOf[id] || [];
      const ys = kids.map(compute);
      y[id] = ys.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : padY + availH / 2;
      return y[id];
    }
    nodes.forEach((n) => compute(n.id));
    return y;
  }, [nodes, childrenOf, leaves]);

  const legendItems = useMemo(
    () => levelLegendItems(nodes, levelLabels),
    [nodes, t],
  );
  const legFilter =
    legend.filter !== null ? legendItems[legend.filter]?.id ?? null : null;
  const selectedLevel = selected !== null ? nodeById[selected]?.level : undefined;
  const legendSelectedIndex =
    legend.selected ??
    legendIndexOfCategory(
      legendItems,
      selectedLevel !== undefined ? `l${selectedLevel}` : null,
    );
  const hoveredPath = effective
    ? new Set([effective, ...ancestorsOf(effective)])
    : null;

  return (
    <div className="w-full">
      <div className="relative">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${dynamicH}`} className="w-full h-auto" style={{ overflow: "visible" }}>
          {nodes
            .filter((n) => n.parent)
            .map((n) => {
              const parent = nodeById[n.parent!];
              const px = xForLevel(parent.level) + nodeW;
              const py = yByNode[parent.id];
              const cx = xForLevel(n.level);
              const cy = yByNode[n.id];
              const midX = (px + cx) / 2;
              const onPath =
                hoveredPath && hoveredPath.has(n.id) && hoveredPath.has(parent.id);
              const legDim =
                legFilter !== null &&
                levelForFilter[legFilter] !== n.level &&
                levelForFilter[legFilter] !== parent.level;
              const dim = (hoveredPath && !onPath) || legDim;
              return (
                <path
                  key={n.id}
                  d={`M ${px} ${py} C ${midX} ${py}, ${midX} ${cy}, ${cx} ${cy}`}
                  fill="none"
                  stroke={onPath ? "var(--chart-1)" : "var(--chart-3)"}
                  strokeWidth={onPath ? 1.75 : 1.2}
                  strokeOpacity={dim ? 0.15 : 0.85}
                  strokeLinecap="round"
                  style={{ transition: "all 0.15s" }}
                />
              );
            })}
          {nodes.map((n) => {
            const x = xForLevel(n.level);
            const y = yByNode[n.id];
            const isHovered = effective === n.id;
            const onPath = hoveredPath && hoveredPath.has(n.id);
            const isRoot = n.level === 0;
            const legDim = legFilter !== null && levelForFilter[legFilter] !== n.level;
            const dim = (hoveredPath && !onPath) || legDim;
            const stroke = isRoot
              ? "var(--chart-1)"
              : n.level === 1
                ? "var(--chart-2)"
                : "var(--chart-3)";
            return (
              <g
                key={n.id}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) node.setHovered(n.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) node.setHovered(null);
                }}
                onClick={(e) =>
                  node.select(n.id, {
                    kind: "node",
                    id: n.id,
                    element: e.currentTarget,
                  })
                }
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.3 : 1}
              >
                {(isHovered || (isRoot && !hoveredPath)) && (
                  <rect
                    x={x - 2}
                    y={y - nodeH / 2 - 2}
                    width={nodeW + 4}
                    height={nodeH + 4}
                    rx="8"
                    fill="none"
                    stroke={isRoot ? "var(--chart-1)" : "var(--chart-2)"}
                    strokeWidth="1"
                    opacity={isRoot && !hoveredPath ? 0.4 : 0.6}
                  />
                )}
                <rect
                  x={x}
                  y={y - nodeH / 2}
                  width={nodeW}
                  height={nodeH}
                  rx="6"
                  fill="var(--card)"
                  stroke={stroke}
                  strokeWidth={onPath || isHovered ? 1.5 : 1}
                />
                {isRoot && <circle cx={x + 10} cy={y} r="3" fill={rootTone} />}
                <foreignObject
                  x={x + (isRoot ? 16 : 0)}
                  y={y - nodeH / 2}
                  width={nodeW - (isRoot ? 16 : 0)}
                  height={nodeH}
                  overflow="visible"
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: dim ? "var(--muted)" : "var(--fg)",
                      fontSize: "11px",
                      fontWeight: isRoot ? 600 : 500,
                      lineHeight: 1.15,
                      textAlign: "center",
                      overflowWrap: "anywhere",
                      padding: "0 6px",
                    }}
                  >
                    {stripInlineMarkdown(n.label)}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>
      <SwipeScroll className="mt-4 pt-4 border-t border-dashed border-[var(--border-dashed)]">
        <Legend
          items={legendItems}
          hoveredIndex={legend.hovered}
          onHoverChange={legend.setHovered}
          selectedIndex={legendSelectedIndex}
          onSelect={legend.select}
          onLabelRef={legend.setLabelRef}
          className="flex flex-row gap-1"
        >
          <LegendItem className="flex shrink-0 items-center gap-2 px-2.5 py-1.5">
            <LegendMarker />
            <LegendLabel />
            <LegendValue />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
  );
}

export default function ModuleHierarchyTree({
  data,
  variant,
  columns,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleHierarchyTreeData;
  variant?: "columnar" | "bracket" | "node-link";
  columns?: Column[];
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const effectiveVariant = variant ?? data?.variant ?? "columnar";
  const effectiveColumns = columns ?? data?.columns;
  const nodes = data?.nodes ?? [];

  return (
    <SwipeScroll className={cn("w-full", className)} style={importsTokenScope(grayscale, accent)}>
      <div className="min-w-[760px]">
        {effectiveVariant === "bracket" && <BracketView nodes={nodes} />}
        {effectiveVariant === "node-link" && <NodeLinkView nodes={nodes} rootTone={accentToneOr(grayscale, accent, "var(--emerald, #10b981)")} />}
        {effectiveVariant === "columnar" && (
          <ColumnarView nodes={nodes} columns={effectiveColumns} />
        )}
      </div>
    </SwipeScroll>
  );
}
